import { Trans } from '@lingui/react/macro';
import { type ReactNode, useState } from 'react';

import SortDownIcon from '@/assets/svg/bx-sort-down.svg?react';
import SortUpIcon from '@/assets/svg/bx-sort-up.svg?react';
import { Button, Toggle } from '@/components/buttons';
import { cleanFilter, encodeFilterFieldName } from '@/components/filters/fieldName';
import { useAppForm } from '@/components/Form';
import type { FilterOperatorValue } from '@/components/InstantSearch';

type FilterFormValues = Record<string, FilterOperatorValue | null | undefined>;
type SortDirection = 'asc' | 'desc' | null;

type DataTableColumnTitlePopoverContentProps = {
  dataKey: string;
  isSortable: boolean;
  filter: FilterOperatorValue | null | undefined;
  sortDirection: 'asc' | 'desc' | false;
  children: ReactNode;
  onClose: () => void;
  onApply: (filter: FilterOperatorValue | undefined, sort: SortDirection) => void;
};

const DataTableColumnTitlePopoverContent = ({
  dataKey,
  isSortable,
  filter,
  sortDirection,
  children,
  onClose,
  onApply,
}: DataTableColumnTitlePopoverContentProps) => {
  const [sortValue, setSortValue] = useState<SortDirection>(
    sortDirection === false ? null : sortDirection,
  );
  const fieldName = encodeFilterFieldName(dataKey);

  const form = useAppForm({
    defaultValues: { [fieldName]: filter } as FilterFormValues,
    onSubmit: ({ value }) => {
      const cleanedFilter = cleanFilter(value[fieldName]);
      onApply(cleanedFilter, sortValue);
      onClose();
    },
  });

  return (
    <div className="flex w-75 flex-col" data-testid="filter-dropdown">
      {isSortable && (
        <div className="flex flex-col items-start gap-1 px-6 py-3.5">
          <Toggle
            variant="ghost"
            shape="square"
            pressed={sortValue === 'asc'}
            onClick={() => setSortValue('asc')}
            className="gap-1.5"
          >
            <SortDownIcon />
            <Trans>Sort A to Z</Trans>
          </Toggle>
          <Toggle
            variant="ghost"
            shape="square"
            pressed={sortValue === 'desc'}
            onClick={() => setSortValue('desc')}
            className="gap-1.5"
          >
            <SortUpIcon />
            <Trans>Sort Z to A</Trans>
          </Toggle>
        </div>
      )}
      <form.AppForm>
        <div className="px-6 py-3.5">{children}</div>
        <div className="flex justify-between bg-primary px-6 py-3.5">
          <Button onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <form.SubmitButton variant="on-primary">
            <Trans>Apply</Trans>
          </form.SubmitButton>
        </div>
      </form.AppForm>
    </div>
  );
};

export default DataTableColumnTitlePopoverContent;
